import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid2 as Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";

import VisitRequetSlot from "~/components/command/VisitRequestSlot";
import CreateVisitRequest from "~/components/scheduler/CreateVisitRequest";
import { useState } from "react";
import WarningIcon from '@mui/icons-material/Warning';
import DragEventsModalErrors from "./DragEventsErrorsModal";
import { CalendarError, CustomMbscCalendarEventData, LocalVisitRequest } from "~/utils/eventsHelper";
import { find } from "lodash";
import { castDate } from "@caredna-marlin/utils/dates";
import { DriveTimeOptimizationResource, LocalCalendarBlock, LocalVisit, SkippedVisitResource } from "~/hooks/visitDragDropReducer";
import OptimizationReview from "./OptimizationReview";
import { CommandIndexCareRequirementVisitsQuery, CommandIndexProvidersQuery } from "~/graphql/__generated__/graphql";
import { OptimizationIssueStats } from "./optimizationReviewUtils";

interface Props {
  date: Date | string;
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  driveTimeLoading: boolean;
  visitRequests: LocalVisitRequest[];
  localVisits: LocalVisit[],
  visitEvents?: CustomMbscCalendarEventData[];
  isDirty: boolean;
  isSavingChanges: boolean;
  errorsAndWarnings?: Map<string, (CalendarError | null)[]>;
  onReset: () => void;
  onSave: () => void;
  onRetryDriveTimes?: () => void;
  isRetryingDriveTimes?: boolean;
  showGhosts: boolean;
  onToggleGhosts: (show: boolean) => void;
  driveTimeOptimizations?: DriveTimeOptimizationResource[]
  optimizationIssueStats: OptimizationIssueStats;
  isPatientView?: boolean;
  hasMoreActivePatients?: boolean;
  activePatientsDisplayLimit?: number;
  localCalendarBlocks: LocalCalendarBlock[];
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"];
  skippedVisitResources?: SkippedVisitResource[];
  optimizerLogId?: number | null; 
}

const CalendarHeaderContent: React.FC<Props> = ({
  date,
  providers,
  visitRequests,
  visitEvents,
  isSavingChanges,
  driveTimeLoading,
  onReset,
  errorsAndWarnings,
  onSave,
  isDirty,
  onRetryDriveTimes,
  driveTimeOptimizations,
  showGhosts,
  onToggleGhosts,
  isRetryingDriveTimes = false,
  isPatientView = false,
  hasMoreActivePatients = false,
  activePatientsDisplayLimit,
  optimizationIssueStats,
  skippedVisitResources,
  optimizerLogId,
}) => {
  const [openVisitRequestModal, setOpenVisitRequestModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const hasErrors = Array.from(errorsAndWarnings?.values() ?? []).some(
    (errors) => (errors ?? []).some((e) => e !== null)
  );

  const calendarDateTime = castDate(date);

  const shouldDisplayOptimizationReview = !!skippedVisitResources?.length || !!driveTimeOptimizations?.length;

  return (
    <Grid container sx={{ mt: 2 }} flexDirection="row" alignItems="flex-end">
      <Grid flexDirection='column' style={{ display: "flex", flex: "1" }}>
        <Typography sx={{ mb: 1 }} variant="subtitle2">
          {visitRequests.length ? `Pending to assign  ` : ''}
          <Button
            variant="contained"
            sx={{ ml: 1 }}
            onClick={() => setOpenVisitRequestModal(true)}
          >
            Add Visit
          </Button>
        </Typography>
        <Grid padding={1} container gap={2}>
          {visitRequests?.map((visitRequest) => {
            const isDraggable = !find(visitEvents, (ve) => ve.id === visitRequest.id)
            return (
              <VisitRequetSlot
                key={`visit-request-${visitRequest.id}`}
                data={visitRequest}
                isDraggable={isDraggable && !isPatientView}
                isPatientView={isPatientView}
              />
            );
          })}
        </Grid>
      </Grid>

      {(isDirty || shouldDisplayOptimizationReview) && (
        <Paper
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1,
            mb: 1
          }}
        >
            {(!isPatientView && shouldDisplayOptimizationReview) && (
              <OptimizationReview
                loading={driveTimeLoading}
                issueStats={optimizationIssueStats}
                providers={providers}
                driveTimeOptimizations={driveTimeOptimizations}
                onToggleGhosts={onToggleGhosts}
                showGhosts={showGhosts}
                skippedVisitResources={skippedVisitResources}
                optimizerLogId={optimizerLogId}
              />
            )}
            <Grid position="relative" flexDirection="row">
              {hasErrors && (
                <Tooltip placement="top" title="There's warnings on your latest changes, please click to preview.">
                  <IconButton sx={{ mr: 2 }} aria-label="close" onClick={() => setShowErrorModal(true)}>
                    <WarningIcon color="warning" />
                  </IconButton>
                </Tooltip>
              )}
              
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {driveTimeLoading && (
                  <Typography variant="caption" sx={{ mr: 3 }}>
                      <CircularProgress sx={{ mr: 3, verticalAlign: "middle" }} size="15px" />
                      Updating drive times ...
                    </Typography>
                  )}
                  {isDirty && (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => {
                        if (driveTimeOptimizations?.length) {
                          const confirmed = window.confirm('Are you sure you want to save these changes?');
                          if (confirmed) {
                            onSave();
                          }
                        } else {
                          onSave();
                        }
                      }}
                      sx={{ mr: 2 }}
                      disabled={driveTimeLoading}
                      loading={isSavingChanges}
                    >
                      Save
                    </Button>
                  )}
                  <Button
                    onClick={onReset}
                  >
                    Reset
                  </Button>
                  
                </Box>
            </Grid>
        </Paper>
      )}

      {isPatientView && hasMoreActivePatients && (
        <Grid>
          <Card
            sx={{
              p: 1,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="warning.dark">
              {activePatientsDisplayLimit
                ? `Showing first ${activePatientsDisplayLimit} active patients without scheduled visits; additional unscheduled active patients are not shown.`
                : 'Some active patients without scheduled visits may not be displayed.'}
            </Typography>
          </Card>
        </Grid>
      )}

      <CreateVisitRequest
        key={(calendarDateTime)?.getTime()}
        open={openVisitRequestModal}
        handleClose={() => setOpenVisitRequestModal(false)}
        calendarDateTime={calendarDateTime}
        onSuccess={() => {
        }}
      />
      <DragEventsModalErrors
        errorsAndWarnings={errorsAndWarnings}
        open={showErrorModal}
        handleClose={() => setShowErrorModal(false)}
        onRetryDriveTimes={onRetryDriveTimes}
        isRetrying={isRetryingDriveTimes}
      />
    </Grid>
  )
}

export default CalendarHeaderContent;
